import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Card, CardContent, CircularProgress, Link, Stack, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import BlockIcon from '@mui/icons-material/Block';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

import { fetchRequest as rerunRequest } from '../../redux/actions/discovery/rerunGoal';
import { fetchRequest as startTournament } from '../../redux/actions/discovery/startTournament';
import { promoteRequest } from '../../redux/actions/promotion/promote';

/* Each phase's glyph (the row's identity). */
const PHASE_ICON = {
  discover: TravelExploreIcon,
  fit: ModelTrainingIcon,
  deploy: CloudUploadIcon,
  live: MonitorHeartIcon,
};

/* State → colour + node glyph. Strength of colour mirrors progress: green
   done, blue working, amber needs-you, red failed, grey waiting/off. */
const stateMeta = (theme) => ({
  done: { color: theme.palette.success.main, Icon: CheckCircleIcon, chip: 'Done', tone: 'success' },
  active: { color: theme.palette.info.main, Icon: null, chip: 'In progress', tone: 'info' },
  review: { color: theme.palette.warning.main, Icon: HourglassTopIcon, chip: 'Needs you', tone: 'warning' },
  failed: { color: theme.palette.error.main, Icon: ErrorOutlineIcon, chip: 'Failed', tone: 'error' },
  blocked: { color: theme.palette.warning.main, Icon: BlockIcon, chip: 'Blocked', tone: 'warning' },
  off: { color: theme.palette.text.disabled, Icon: PauseCircleOutlineIcon, chip: 'Off', tone: 'default' },
  pending: { color: theme.palette.text.disabled, Icon: RadioButtonUncheckedIcon, chip: 'Waiting', tone: 'default' },
});

/**
 * The goal's lifecycle, told as one live, visual chain:
 *   Discover → Auto-fit → Deploy → Monitor
 * Each phase shows its state, plain-language reasoning, metrics and the one
 * next action — so the user always knows what's happening and what's next.
 */
const GoalActivity = ({ goalId, onOpenForecast, onGoToModels, onCta, orientation = 'horizontal' }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const state = useSelector((s) => s.discovery_goal_activity);
  const rerunState = useSelector((s) => s.discovery_rerun_goal);
  const startState = useSelector((s) => s.discovery_start_tournament);
  const promoteState = useSelector((s) => s.model_promote);

  const data = state?.response;
  const phases = useMemo(() => data?.phases || [], [data]);

  // Fetch/refresh/clear of the activity state is owned by GoalDetailPage
  // (it must stay live on every section for the guided tour, not just
  // Overview where this card renders) - this component only consumes it.

  const meta = stateMeta(theme);

  const ctaBusy = (action) => (
    (action === 'rerun' && rerunState?.isLoading)
    || (action === 'fit' && startState?.isLoading)
    || (action === 'deploy' && promoteState?.isLoading)
  );

  const runCta = (phase) => {
    const cta = phase.cta;
    if (!cta) return;
    if (onCta) { onCta(phase); return; }
    switch (cta.action) {
      case 'rerun':
        dispatch(rerunRequest({ goalId }));
        break;
      case 'fit':
        dispatch(startTournament({ goalId }));
        break;
      case 'deploy': {
        // Deploy lives in the Model fitting section (with the model context).
        // From the chain we just take the user there — one canonical action.
        if (onGoToModels) { onGoToModels(); return; }
        const { model_id: modelId, training_id: trainingId } = phase.refs || {};
        if (!modelId || !trainingId) return;
        dispatch(promoteRequest({
          modelId,
          trainingId,
          reason: 'Discovery best model',
        }));
        break;
      }
      case 'forecast':
        if (onOpenForecast) onOpenForecast();
        break;
      default:
        break;
    }
  };

  const ctaDisabled = (phase) => {
    if (onCta) return false;
    if (phase.cta?.action === 'deploy'
      && !(phase.refs?.model_id && phase.refs?.training_id)) return true;
    return Boolean(ctaBusy(phase.cta?.action));
  };

  // The one next-step link for a phase (shared by both layouts).
  const renderCta = (phase) => {
    if (!phase.cta) return null;
    const goModels = phase.cta.action === 'deploy' && Boolean(onGoToModels);
    const disabled = goModels ? false : ctaDisabled(phase);
    const label = goModels
      ? 'Review & deploy'
      : (ctaBusy(phase.cta.action) ? 'Working…' : phase.cta.label);
    if (disabled) {
      return (
        <Typography variant="caption" sx={{ fontSize: 10.5, color: 'text.disabled' }}>
          {label}
        </Typography>
      );
    }
    return (
      <Link
        component="button"
        underline="hover"
        onClick={() => runCta(phase)}
        sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2, color: 'primary.main' }}
      >
        {label}
      </Link>
    );
  };

  const isVertical = orientation === 'vertical';

  if (isVertical) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary', lineHeight: 1, display: 'block', mb: 1.5 }}
          >
            Activity
          </Typography>

          {state?.isLoading && !data && (
            <Stack alignItems="center" sx={{ py: 1.5 }}><CircularProgress size={18} /></Stack>
          )}

          <Box>
            {phases.map((phase, i) => {
              const m = meta[phase.state] || meta.pending;
              const PhaseIcon = PHASE_ICON[phase.id] || RadioButtonUncheckedIcon;
              const isLast = i === phases.length - 1;
              const active = phase.state === 'active';
              const BadgeIcon = ['done', 'failed', 'review', 'blocked'].includes(phase.state)
                ? m.Icon : null;
              const cta = renderCta(phase);
              return (
                <Stack key={phase.id} direction="row" spacing={1.5} alignItems="stretch">
                  {/* Rail: node + connecting line down to the next phase */}
                  <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                    <Box sx={{
                      width: 30, height: 30, borderRadius: '50%', position: 'relative',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `2px solid ${alpha(m.color, 0.85)}`,
                      backgroundColor: alpha(m.color, 0.1),
                      boxShadow: active ? `0 0 0 4px ${alpha(m.color, 0.14)}` : 'none',
                      flexShrink: 0,
                    }}>
                      {active
                        ? <CircularProgress size={14} sx={{ color: m.color }} />
                        : <PhaseIcon sx={{ fontSize: 16, color: m.color }} />}
                      {BadgeIcon && (
                        <BadgeIcon sx={{
                          position: 'absolute', right: -4, bottom: -4, fontSize: 13,
                          color: m.color, bgcolor: theme.palette.background.paper,
                          borderRadius: '50%',
                        }} />
                      )}
                    </Box>
                    {!isLast && (
                      <Box sx={{
                        flexGrow: 1, width: 0, my: 0.5,
                        borderLeft: `2px ${phase.state === 'done' ? 'solid' : 'dashed'} ${alpha(
                          phase.state === 'done' ? theme.palette.success.main : theme.palette.divider, 0.9)}`,
                      }} />
                    )}
                  </Stack>

                  {/* Content */}
                  <Box sx={{ pb: isLast ? 0 : 2, minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                        {phase.title}
                      </Typography>
                      <Typography sx={{
                        fontSize: 10, fontWeight: 700, color: m.color,
                        textTransform: 'uppercase', letterSpacing: 0.4,
                      }}>
                        {m.chip}
                      </Typography>
                    </Stack>
                    {phase.summary && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.3 }}>
                        {phase.summary}
                      </Typography>
                    )}
                    {phase.reasoning && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, opacity: 0.85, lineHeight: 1.3 }}>
                        {phase.reasoning}
                      </Typography>
                    )}
                    {cta && <Box sx={{ mt: 0.5 }}>{cta}</Box>}
                  </Box>
                </Stack>
              );
            })}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1, px: 1.75, '&:last-child': { pb: 1 } }}>
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary', lineHeight: 1, display: 'block', mb: 0.75 }}
        >
          Activity
        </Typography>

        {state?.isLoading && !data && (
          <Stack alignItems="center" sx={{ py: 1.5 }}><CircularProgress size={18} /></Stack>
        )}

        <Stack direction="row" alignItems="flex-start" sx={{ overflowX: 'auto' }}>
          {phases.map((phase, i) => {
            const m = meta[phase.state] || meta.pending;
            const PhaseIcon = PHASE_ICON[phase.id] || RadioButtonUncheckedIcon;
            const isLast = i === phases.length - 1;
            const active = phase.state === 'active';

            const detail = (
              <Box sx={{ p: 0.25 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  {phase.title} · {m.chip}
                </Typography>
                {phase.summary && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                    {phase.summary}
                  </Typography>
                )}
                {phase.reasoning && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                    {phase.reasoning}
                  </Typography>
                )}
                {(phase.metrics || []).map((mt) => (
                  <Typography key={mt.label} variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                    {mt.label}: {mt.value}
                  </Typography>
                ))}
              </Box>
            );

            let cta = null;
            if (phase.cta) {
              const goModels = phase.cta.action === 'deploy' && Boolean(onGoToModels);
              const disabled = goModels ? false : ctaDisabled(phase);
              const label = goModels
                ? 'Review & deploy'
                : (ctaBusy(phase.cta.action) ? 'Working…' : phase.cta.label);
              cta = disabled ? (
                <Typography variant="caption" sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                  {label}
                </Typography>
              ) : (
                <Link
                  component="button"
                  underline="hover"
                  onClick={() => runCta(phase)}
                  sx={{ fontSize: 10.5, fontWeight: 700, lineHeight: 1.2, color: 'primary.main' }}
                >
                  {label}
                </Link>
              );
            }

            // State badge on the node corner conveys status at a glance, so
            // we don't need a repeated "Done" text row under every step.
            const BadgeIcon = ['done', 'failed', 'review', 'blocked'].includes(phase.state)
              ? m.Icon : null;
            return (
              <React.Fragment key={phase.id}>
                <Stack alignItems="center" spacing={0.25} sx={{ flex: '1 1 0', minWidth: 76, px: 0.25 }}>
                  <Tooltip title={detail} arrow placement="top">
                    <Box sx={{
                      width: 26, height: 26, borderRadius: '50%', position: 'relative',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `2px solid ${alpha(m.color, 0.85)}`,
                      backgroundColor: alpha(m.color, 0.1),
                      boxShadow: active ? `0 0 0 4px ${alpha(m.color, 0.14)}` : 'none',
                      cursor: 'default', flexShrink: 0,
                    }}>
                      {active
                        ? <CircularProgress size={13} sx={{ color: m.color }} />
                        : <PhaseIcon sx={{ fontSize: 15, color: m.color }} />}
                      {BadgeIcon && (
                        <BadgeIcon sx={{
                          position: 'absolute', right: -4, bottom: -4, fontSize: 13,
                          color: m.color, bgcolor: theme.palette.background.paper,
                          borderRadius: '50%',
                        }} />
                      )}
                    </Box>
                  </Tooltip>
                  <Typography
                    align="center"
                    sx={{ fontWeight: 700, fontSize: 10.5, lineHeight: 1.1, mt: 0.5 }}
                  >
                    {phase.title}
                  </Typography>
                  {active && (
                    <Typography sx={{ fontSize: 9, fontWeight: 600, color: m.color, lineHeight: 1 }}>
                      {m.chip}
                    </Typography>
                  )}
                  {cta && <Box sx={{ mt: 0.25 }}>{cta}</Box>}
                </Stack>
                {!isLast && (
                  <Box sx={{
                    flex: '1 1 16px', minWidth: 8, alignSelf: 'flex-start', mt: '12px',
                    borderTop: `2px ${phase.state === 'done' ? 'solid' : 'dashed'} ${alpha(
                      phase.state === 'done' ? theme.palette.success.main : theme.palette.divider, 0.9)}`,
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default GoalActivity;
